import React, { useEffect, useRef, useCallback } from 'react';
import * as THREE from 'three';
import { gsap } from 'gsap';

interface TemplatePreviewProps {
  templateName: string;
  templateType: 'cv' | 'cl';
}

const TemplatePreview: React.FC<TemplatePreviewProps> = ({ templateName, templateType }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const meshRef = useRef<THREE.Mesh | null>(null);
  const currentTemplateRef = useRef<string>(templateName);
  const isAnimatingRef = useRef<boolean>(false);
  const animationRef = useRef<any>(null);

  // Convert template name to image filename
  const getImagePath = (name: string): string => {
    const prefix = templateType === 'cv' ? 'cv' : 'cl';
    const fileName = name.toLowerCase().replace(/\s+/g, '-');
    return `/images/templates/${prefix}-${fileName}.webp`;
  };

  // Load texture from image
  const loadTexture = (imagePath: string, shouldMirror: boolean = false): Promise<THREE.Texture> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';

      img.onload = () => {
        const canvasWidth = 1200;
        const canvasHeight = 1697;

        const canvas = document.createElement('canvas');
        canvas.width = canvasWidth;
        canvas.height = canvasHeight;
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }

        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvasWidth, canvasHeight);

        if (shouldMirror) {
          ctx.translate(canvasWidth, 0);
          ctx.scale(-1, 1);
          ctx.drawImage(img, 0, 0, canvasWidth, canvasHeight);
        } else {
          ctx.drawImage(img, 0, 0, canvasWidth, canvasHeight);
        }

        const texture = new THREE.CanvasTexture(canvas);
        texture.needsUpdate = true;
        // Improve texture quality
        texture.minFilter = THREE.LinearFilter;
        texture.magFilter = THREE.LinearFilter;
        texture.anisotropy = 16; // Maximum anisotropic filtering for crisp textures
        resolve(texture);
      };

      img.onerror = () => {
        reject(new Error(`Failed to load image: ${imagePath}`));
      };

      img.src = imagePath;
    });
  };

  // Load initial template
  const loadInitialTemplate = useCallback(async () => {
    if (!sceneRef.current) return;

    try {
      const imagePath = getImagePath(currentTemplateRef.current);
      const texture = await loadTexture(imagePath);

      const canvasWidth = 1200;
      const canvasHeight = 1697;
      const aspectRatio = canvasWidth / canvasHeight;
      const planeWidth = 2.8; // Increased from 2 to 2.8 for larger display
      const planeHeight = planeWidth / aspectRatio;

      const geometry = new THREE.PlaneGeometry(planeWidth, planeHeight);
      // Use MeshBasicMaterial for full brightness without lighting effects
      const material = new THREE.MeshBasicMaterial({
        map: texture,
        side: THREE.DoubleSide,
        transparent: false,
      });

      const mesh = new THREE.Mesh(geometry, material);
      mesh.rotation.y = 0;
      meshRef.current = mesh;
      sceneRef.current.add(mesh);
    } catch (error) {
      console.error('Error loading template:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateType]);

  // Rotate to new template
  const rotateToTemplate = useCallback(async (newTemplate: string) => {
    if (!meshRef.current || isAnimatingRef.current || currentTemplateRef.current === newTemplate) {
      return;
    }

    isAnimatingRef.current = true;

    try {
      const imagePath = getImagePath(newTemplate);

      // Preload both normal and mirrored versions
      const normalTexture = await loadTexture(imagePath, false);
      const mirroredTexture = await loadTexture(imagePath, true);

      const startRotation = meshRef.current.rotation.y;
      const targetRotation = startRotation + Math.PI;
      let hasSwapped = false;

      // Kill any existing animation
      if (animationRef.current) {
        animationRef.current.kill();
      }

      // Animate rotation
      animationRef.current = gsap.to(meshRef.current.rotation, {
        y: targetRotation,
        duration: 2.5,
        ease: "power2.inOut",
        onUpdate: () => {
          if (!meshRef.current) return;

          const currentRotation = meshRef.current.rotation.y;
          const rotationProgress = currentRotation - startRotation;

          // Swap texture at 90 degrees - exactly like jobesta-landing
          if (!hasSwapped && rotationProgress >= Math.PI / 2) {
            hasSwapped = true;

            // Determine mirroring based on current rotation (same logic as jobesta-landing)
            const normalizedRotation = currentRotation % (Math.PI * 2);
            const shouldMirror = normalizedRotation < Math.PI;

            // Select the appropriate pre-loaded texture
            const newTexture = shouldMirror ? mirroredTexture : normalTexture;

            if (meshRef.current.material instanceof THREE.MeshBasicMaterial) {
              if (meshRef.current.material.map) {
                meshRef.current.material.map.dispose();
              }
              meshRef.current.material.map = newTexture;
              meshRef.current.material.needsUpdate = true;
            }

            currentTemplateRef.current = newTemplate;
          }
        },
        onComplete: () => {
          if (meshRef.current) {
            meshRef.current.rotation.y = meshRef.current.rotation.y % (Math.PI * 2);

            // Clean up the unused texture
            const normalizedRotation = meshRef.current.rotation.y % (Math.PI * 2);
            const usedMirrored = normalizedRotation < Math.PI;
            if (usedMirrored) {
              normalTexture.dispose();
            } else {
              mirroredTexture.dispose();
            }
          }
          isAnimatingRef.current = false;
        }
      });

      // Animate slight bounce
      gsap.to(meshRef.current.position, {
        y: "+=0.2",
        duration: 1.25,
        yoyo: true,
        repeat: 1,
        ease: "sine.inOut"
      });
    } catch (error) {
      console.error('Error rotating to template:', error);
      isAnimatingRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateType]);

  // Initialize Three.js scene
  useEffect(() => {
    if (!containerRef.current) return;

    // Scene setup
    const scene = new THREE.Scene();
    sceneRef.current = scene;

    // Camera setup
    const camera = new THREE.PerspectiveCamera(75, 1, 0.1, 1000);
    camera.position.z = 3.2; // Moved back slightly to accommodate larger template
    cameraRef.current = camera;

    // Renderer setup with better quality
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      powerPreference: "high-performance"
    });
    renderer.setClearColor(0xf5f5f5, 1); // Light gray background instead of transparent
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2)); // Better quality on high-DPI displays
    rendererRef.current = renderer;

    const updateSize = () => {
      if (!containerRef.current) return;
      const width = containerRef.current.clientWidth;
      const height = containerRef.current.clientHeight;
      renderer.setSize(width, height);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
    };

    updateSize();
    containerRef.current.appendChild(renderer.domElement);

    // Much brighter lighting for better visibility
    const ambientLight = new THREE.AmbientLight(0xffffff, 1.2);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 1.0);
    directionalLight.position.set(5, 5, 5);
    scene.add(directionalLight);

    const backLight = new THREE.DirectionalLight(0xffffff, 0.8);
    backLight.position.set(-5, -5, -5);
    scene.add(backLight);

    // Add front light for even better visibility
    const frontLight = new THREE.DirectionalLight(0xffffff, 0.5);
    frontLight.position.set(0, 0, 5);
    scene.add(frontLight);

    // Animation loop
    const animate = () => {
      requestAnimationFrame(animate);
      renderer.render(scene, camera);
    };
    animate();

    // Handle resize
    const handleResize = () => updateSize();
    window.addEventListener('resize', handleResize);

    // Load initial template
    loadInitialTemplate();

    // Cleanup
    const container = containerRef.current;
    return () => {
      window.removeEventListener('resize', handleResize);
      if (container && renderer.domElement) {
        container.removeChild(renderer.domElement);
      }
      if (meshRef.current) {
        scene.remove(meshRef.current);
        if (meshRef.current.geometry) meshRef.current.geometry.dispose();
        if (meshRef.current.material instanceof THREE.MeshBasicMaterial) {
          if (meshRef.current.material.map) meshRef.current.material.map.dispose();
          meshRef.current.material.dispose();
        }
      }
      renderer.dispose();
    };
  }, [loadInitialTemplate]);

  // Watch for template changes
  useEffect(() => {
    if (templateName !== currentTemplateRef.current) {
      rotateToTemplate(templateName);
    }
  }, [templateName, rotateToTemplate]);

  return (
    <div
      ref={containerRef}
      className="w-full h-96 md:h-[500px] flex justify-center items-center rounded-jobesta shadow-sm border border-jobesta-gray-30"
      style={{ minHeight: '450px', backgroundColor: '#f5f5f5' }}
    />
  );
};

export default TemplatePreview;
